import React, { useRef, useEffect } from 'react';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import '../styles/audio-recorder.css';

/**
 * AudioRecorder Component
 *
 * Provides UI for recording audio with:
 * - Record/Stop controls
 * - Real-time recording timer
 * - Playback preview
 * - Re-record and delete options
 * - Visual feedback during recording
 *
 * @param {Object} props
 * @param {Function} props.onAudioRecorded - Callback when audio is recorded (receives blob)
 * @param {Function} props.onAudioDeleted - Callback when audio is deleted
 * @param {string} props.existingAudioUrl - URL of existing audio (optional)
 * @param {number} props.maxDuration - Max recording duration in seconds (default: 30)
 */
const AudioRecorder = ({ onAudioRecorded, onAudioDeleted, existingAudioUrl, maxDuration = 30 }) => {
  const {
    isRecording,
    isPaused,
    recordingTime,
    audioBlob,
    audioUrl,
    error,
    isPlaying,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    playAudio,
    pauseAudio,
    deleteRecording,
    formattedTime,
  } = useAudioRecorder({ maxDuration });

  // Track if we've already notified parent for this blob
  const notifiedRef = useRef(false);
  const prevBlobRef = useRef(null);

  // Notify parent when audio is recorded (only once per new blob)
  useEffect(() => {
    if (audioBlob && audioBlob !== prevBlobRef.current && !notifiedRef.current) {
      notifiedRef.current = true;
      prevBlobRef.current = audioBlob;
      if (onAudioRecorded) {
        onAudioRecorded(audioBlob);
      }
    }
    // Reset when blob is cleared
    if (!audioBlob) {
      notifiedRef.current = false;
      prevBlobRef.current = null;
    }
  }, [audioBlob, onAudioRecorded]);

  /**
   * Handle delete
   */
  const handleDelete = () => {
    deleteRecording();
    if (onAudioDeleted) {
      onAudioDeleted();
    }
  };

  /**
   * Handle re-record
   */
  const handleReRecord = () => {
    handleDelete();
    startRecording();
  };

  /**
   * Render recording controls
   */
  if (isRecording) {
    return (
      <div className="audio-recorder recording">
        <div className="recording-indicator">
          <span className="recording-dot"></span>
          <span className="recording-text">Recording...</span>
          <span className="recording-time">{formattedTime}</span>
        </div>

        <div className="recording-controls">
          {isPaused ? (
            <button
              type="button"
              className="btn-audio btn-resume"
              onClick={resumeRecording}
              title="Resume recording"
            >
              ▶️ Resume
            </button>
          ) : (
            <button
              type="button"
              className="btn-audio btn-pause"
              onClick={pauseRecording}
              title="Pause recording"
            >
              ⏸️ Pause
            </button>
          )}

          <button
            type="button"
            className="btn-audio btn-stop"
            onClick={stopRecording}
            title="Stop recording"
          >
            ⏹️ Stop
          </button>

          <button
            type="button"
            className="btn-audio btn-cancel"
            onClick={cancelRecording}
            title="Cancel recording"
          >
            🗑️ Cancel
          </button>
        </div>

        <div className="recording-progress">
          <div
            className="recording-progress-bar"
            style={{ width: `${(recordingTime / maxDuration) * 100}%` }}
          ></div>
        </div>

        <p className="recording-hint">
          Maximum duration: {maxDuration}s
        </p>
      </div>
    );
  }

  /**
   * Render playback controls (after recording)
   */
  if (audioUrl || existingAudioUrl) {
    return (
      <div className="audio-recorder recorded">
        <div className="recorded-info">
          <span className="recorded-icon">✓</span>
          <span className="recorded-text">
            {audioUrl ? `Recorded: ${formattedTime}` : 'Audio available'}
          </span>
        </div>

        <div className="playback-controls">
          {isPlaying ? (
            <button
              type="button"
              className="btn-audio btn-pause-playback"
              onClick={pauseAudio}
              title="Pause playback"
            >
              ⏸️ Pause
            </button>
          ) : (
            <button
              type="button"
              className="btn-audio btn-play"
              onClick={playAudio}
              title="Play recording"
            >
              ▶️ Play
            </button>
          )}

          <button
            type="button"
            className="btn-audio btn-re-record"
            onClick={handleReRecord}
            title="Record again"
          >
            🔄 Re-record
          </button>

          <button
            type="button"
            className="btn-audio btn-delete"
            onClick={handleDelete}
            title="Delete recording"
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render initial state (not recording, no audio)
   */
  return (
    <div className="audio-recorder initial">
      {error && (
        <div className="audio-error">
          ⚠️ {error}
        </div>
      )}

      <button
        type="button"
        className="btn-audio btn-record"
        onClick={startRecording}
        title="Start recording"
      >
        🎙️ Record Audio
      </button>

      <p className="recording-hint">
        Click to record (max {maxDuration}s)
      </p>
    </div>
  );
};

export default AudioRecorder;
